import React, { useEffect, useState, useCallback } from "react";
import {
  X, Loader2, Users as UsersIcon, CreditCard, Activity as ActivityIcon, StickyNote, LayoutDashboard,
  Plus, Trash2, Pause, Play, KeyRound, Mail, LogIn, CalendarClock, FileText, Link2, CheckCircle2,
  Pencil, Send, Ban, Tag, Clock, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { platformApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { Modal, DInput, DSelect, DTextarea, money2, fmtDate, fmtDateTime, STATUS_PILL } from "./ui";

const API = process.env.REACT_APP_BACKEND_URL;
const err = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Something went wrong.");

export default function CompanyDrawer({ id, plans, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // {type, payload}

  const load = useCallback(async () => {
    try { setData(await platformApi.getCompany(id)); } catch (e) { err(e); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); onChange(); };

  const patch = async (body, msg) => {
    setBusy(true);
    try { await platformApi.updateCompany(id, body); toast.success(msg); await refresh(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const doImpersonate = async () => {
    setBusy(true);
    try {
      const res = await platformApi.impersonate(id);
      localStorage.setItem("impersonation", JSON.stringify({ companyName: res.companyName, email: res.user.email }));
      // Hard navigation: a full reload re-initialises the auth context with the
      // impersonated user's cookies and avoids the platform guard race.
      window.location.href = "/";
    } catch (e) { err(e); setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this company and ALL its users, invoices and history? This cannot be undone.")) return;
    setBusy(true);
    try { const r = await platformApi.deleteCompany(id); toast.success(`Deleted (${r.deletedUsers} users removed).`); onChange(); onClose(); }
    catch (e) { err(e); setBusy(false); }
  };

  const c = data?.company;
  const suspended = c?.accountStatus === "suspended";

  const TABS = [
    ["overview", "Overview", LayoutDashboard],
    ["users", "Users", UsersIcon],
    ["invoices", "Billing", CreditCard],
    ["activity", "Activity", ActivityIcon],
    ["notes", "Notes", StickyNote],
  ];

  return (
    <div className="fixed inset-0 z-30 flex justify-end" data-testid="platform-company-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0C111B] border-l border-white/10 h-full overflow-y-auto">
        {!data ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-[20px] font-bold text-white">{c.name}</h2>
                <div className="text-[12.5px] text-slate-500">{c.ownerEmail}{c.contactPhone ? ` · ${c.contactPhone}` : ""}</div>
              </div>
              <button onClick={onClose} data-testid="drawer-close" className="p-1.5 rounded-lg text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-4 gap-2.5 mb-4">
              <Stat label="Status" value={<span className={`inline-block px-2 py-0.5 rounded-full border text-[10.5px] font-semibold capitalize ${STATUS_PILL[c.subscriptionStatus]}`}>{c.subscriptionStatus.replace("_", " ")}</span>} />
              <Stat label="MRR" value={<span className="text-emerald-300">{money2(c.mrr)}</span>} />
              <Stat label="Seats (in use)" value={`${c.seats} (${c.userCount})`} />
              <Stat label="Next charge" value={<span className="text-cyan-300" data-testid="drawer-next-charge">{money2(c.nextCharge ?? c.mrr)}</span>} />
            </div>

            {/* Global actions */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <ActionBtn icon={LogIn} label="Log in as" onClick={doImpersonate} disabled={busy} testid="drawer-impersonate" />
              <ActionBtn icon={Mail} label="Email" onClick={() => setModal({ type: "email" })} testid="drawer-email-account" />
              <ActionBtn icon={CalendarClock} label="Grant days" onClick={() => setModal({ type: "grant" })} testid="drawer-grant-days" />
              {suspended ? (
                <ActionBtn icon={Play} label="Reactivate" tone="emerald" onClick={() => patch({ status: "active" }, "Reactivated.")} disabled={busy} testid="drawer-activate" />
              ) : (
                <ActionBtn icon={Pause} label="Suspend" tone="orange" onClick={() => patch({ status: "suspended" }, "Suspended.")} disabled={busy} testid="drawer-suspend" />
              )}
              <ActionBtn icon={Trash2} label="Delete" tone="rose" onClick={del} disabled={busy} testid="drawer-delete" />
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-white/10 mb-4 overflow-x-auto">
              {TABS.map(([key, label, Icon]) => (
                <button key={key} data-testid={`drawer-tab-${key}`} onClick={() => setTab(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === key ? "border-cyan-400 text-cyan-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>

            {tab === "overview" && <OverviewTab company={c} plans={plans} patch={patch} busy={busy} />}
            {tab === "users" && <UsersTab data={data} onAdd={() => setModal({ type: "addUser" })} onInvite={() => setModal({ type: "invite" })} onEdit={(u) => setModal({ type: "editUser", payload: u })} onReset={(u) => setModal({ type: "resetPw", payload: u })} refresh={refresh} />}
            {tab === "invoices" && <InvoicesTab data={data} company={c} onNew={() => setModal({ type: "invoice" })} refresh={refresh} />}
            {tab === "activity" && <ActivityTab activity={data.activity} />}
            {tab === "notes" && <NotesTab company={c} notes={c.adminNotes || []} refresh={refresh} />}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === "addUser" && <AddUserModal id={id} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === "invite" && <InviteModal id={id} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === "editUser" && <EditUserModal user={modal.payload} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === "resetPw" && <ResetPwModal user={modal.payload} onClose={() => setModal(null)} />}
      {modal?.type === "email" && <EmailAccountModal id={id} onClose={() => setModal(null)} />}
      {modal?.type === "grant" && <GrantDaysModal id={id} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      {modal?.type === "invoice" && <InvoiceModal id={id} company={c} plans={plans} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5">
      <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="text-[13px] font-semibold text-white">{value}</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, disabled, tone, testid }) {
  const tones = {
    emerald: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25",
    orange: "bg-orange-500/15 border-orange-500/30 text-orange-300 hover:bg-orange-500/25",
    rose: "bg-rose-500/15 border-rose-500/30 text-rose-300 hover:bg-rose-500/25",
  };
  const cls = tones[tone] || "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[12.5px] font-semibold disabled:opacity-50 ${cls}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

// ---------- Overview ----------
function OverviewTab({ company, plans, patch, busy }) {
  const [contact, setContact] = useState({ name: company.name, contactName: company.contactName || "", ownerEmail: company.ownerEmail || "", contactPhone: company.contactPhone || "" });
  const [discount, setDiscount] = useState(company.discountPct || 0);

  return (
    <div className="space-y-4">
      <Section title="Subscription">
        <div className="grid grid-cols-3 gap-3">
          <DSelect label="Plan" value={company.planId || ""} testid="ov-plan" options={plans.map((p) => [p.id, p.name])} onChange={(e) => patch({ planId: e.target.value }, "Plan updated.")} />
          <DSelect label="Term" value={company.term} testid="ov-term" options={[["monthly", "Monthly"], ["annual", "Annual"]]} onChange={(e) => patch({ term: e.target.value }, "Term updated.")} />
          <SeatsField value={company.seats} onSave={(v) => patch({ seats: v }, "Seats updated.")} />
        </div>
        <div className="grid grid-cols-2 gap-3 items-end mt-3">
          <div className="flex items-end gap-2">
            <DInput label="Discount %" type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} testid="ov-discount" />
            <button onClick={() => patch({ discountPct: Number(discount) || 0 }, "Discount updated.")} disabled={busy}
              className="h-10 px-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12.5px] font-semibold" data-testid="ov-discount-save">Apply</button>
          </div>
          <div className="text-[11.5px] text-slate-500 pb-2.5">
            {company.subscriptionStatus === "trialing" ? `Trial ends ${fmtDate(company.trialEndsAt)}` : company.currentPeriodEnd ? `Renews ${fmtDate(company.currentPeriodEnd)}` : "No active period"}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10">
          <div>
            <div className="text-[12.5px] font-semibold text-slate-200">Auto-pay</div>
            <div className="text-[11px] text-slate-500">Auto-charge each cycle ({company.term === "annual" ? "yearly" : "monthly"}); added seats roll to the next cycle.</div>
          </div>
          <button
            onClick={() => patch({ autopay: !(company.autopay !== false) }, "Auto-pay updated.")}
            disabled={busy}
            data-testid="ov-autopay-toggle"
            role="switch"
            aria-checked={company.autopay !== false}
            className={`relative h-6 w-11 rounded-full transition-colors ${company.autopay !== false ? "bg-emerald-500" : "bg-slate-600"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${company.autopay !== false ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </Section>

      <SeatLedger companyId={company.id} />

      <Section title="Contact information">
        <div className="grid grid-cols-2 gap-3">
          <DInput label="Company name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} testid="ov-company-name" />
          <DInput label="Contact name" value={contact.contactName} onChange={(e) => setContact({ ...contact, contactName: e.target.value })} testid="ov-contact-name" />
          <DInput label="Billing email" type="email" value={contact.ownerEmail} onChange={(e) => setContact({ ...contact, ownerEmail: e.target.value })} testid="ov-owner-email" />
          <DInput label="Phone" value={contact.contactPhone} onChange={(e) => setContact({ ...contact, contactPhone: e.target.value })} testid="ov-phone" />
        </div>
        <button data-testid="ov-save-contact" onClick={() => patch({ name: contact.name, contactName: contact.contactName, ownerEmail: contact.ownerEmail, contactPhone: contact.contactPhone }, "Contact info saved.")} disabled={busy}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12.5px] font-semibold disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save contact info
        </button>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

// Seat-change audit ledger so the seat count reconciles to invoices.
function SeatLedger({ companyId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    let cancelled = false;
    platformApi.seatEvents(companyId).then((e) => { if (!cancelled) setEvents(e); }).catch(() => setEvents([]));
    return () => { cancelled = true; };
  }, [companyId]);
  return (
    <Section title="Seat-change history">
      {events === null ? (
        <div className="text-[12px] text-slate-500 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-[12px] text-slate-500">No seat changes recorded yet.</div>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="seat-ledger">
          {events.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-[12px] px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/10" data-testid={`seat-event-${e.id}`}>
              <div className="min-w-0">
                <span className={`font-bold ${e.delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{e.delta >= 0 ? `+${e.delta}` : e.delta}</span>
                <span className="text-slate-400 ml-1.5">{e.fromSeats}→{e.toSeats} seats</span>
                <span className="text-slate-500 ml-1.5">· {e.reason}</span>
              </div>
              <div className="text-slate-500 text-[10.5px] shrink-0 ml-2">{e.effective === "immediate" ? "now" : "next cycle"} · {fmtDate(e.at)}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function SeatsField({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Seats</label>
      <input type="number" value={v} data-testid="ov-seats" onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (Number(v) !== value) onSave(Number(v) || 1); }}
        className="w-full h-10 px-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30" />
    </div>
  );
}

// ---------- Users ----------
function UsersTab({ data, onAdd, onInvite, onEdit, onReset, refresh }) {
  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await platformApi.deleteUser(u.id); toast.success("User removed."); refresh(); } catch (e) { err(e); }
  };
  const resetEmail = async (u) => {
    try { const r = await platformApi.resetPasswordEmail(u.id); toast.success(r.email_sent ? "Reset link emailed." : "Reset link generated (email not configured)."); if (!r.email_sent && r.reset_url) navigator.clipboard?.writeText(r.reset_url); } catch (e) { err(e); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button data-testid="users-add" onClick={onAdd} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12.5px] font-semibold"><Plus className="h-4 w-4" /> Add user</button>
        <button data-testid="users-invite" onClick={onInvite} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-slate-200 text-[12.5px] font-semibold hover:bg-white/5"><Send className="h-4 w-4" /> Invite by email</button>
      </div>
      <div className="rounded-xl border border-white/10 divide-y divide-white/[0.06]">
        {data.users.map((u) => (
          <div key={u.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`user-row-${u.id}`}>
            <div className="min-w-0">
              <div className="text-[13px] text-slate-200 truncate">{u.name || u.email}</div>
              <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[10.5px] px-2 py-0.5 rounded bg-white/5 text-slate-400 capitalize mr-1">{u.role}</span>
              <IconBtn icon={Pencil} title="Edit" onClick={() => onEdit(u)} testid={`user-edit-${u.id}`} />
              <IconBtn icon={KeyRound} title="Set password" onClick={() => onReset(u)} testid={`user-reset-${u.id}`} />
              <IconBtn icon={Mail} title="Email reset link" onClick={() => resetEmail(u)} testid={`user-reset-email-${u.id}`} />
              <IconBtn icon={Trash2} title="Remove" tone="rose" onClick={() => removeUser(u)} testid={`user-delete-${u.id}`} />
            </div>
          </div>
        ))}
        {!data.users.length && <div className="px-3 py-6 text-center text-[12.5px] text-slate-500">No users.</div>}
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, tone, testid }) {
  return (
    <button title={title} data-testid={testid} onClick={onClick}
      className={`p-1.5 rounded-md ${tone === "rose" ? "text-rose-300 hover:bg-rose-500/10" : "text-slate-400 hover:bg-white/5"}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// ---------- Invoices ----------
// ---------- Promo code (read-only reference + platform-admin apply/remove) ----------
function PromoCodeSection({ company, refresh }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const active = company?.promoCode && company?.promoDiscountPercent != null && company?.promoExpiresAt
    && new Date(company.promoExpiresAt).getTime() > Date.now();
  const daysRemaining = active ? Math.ceil((new Date(company.promoExpiresAt).getTime() - Date.now()) / 86400000) : null;

  const startEdit = () => {
    setCode(company?.promoCode || "");
    setPct(company?.promoDiscountPercent ?? "");
    setExpiresAt(company?.promoExpiresAt ? String(company.promoExpiresAt).slice(0, 16) : "");
    setEditing(true);
  };

  const apply = async () => {
    if (!code.trim() || pct === "" || !expiresAt) { toast.error("Code, discount %, and expiry are all required."); return; }
    setBusy(true);
    try {
      await platformApi.applyPromo(company.id, { promoCode: code.trim(), promoDiscountPercent: Number(pct), promoExpiresAt: new Date(expiresAt).toISOString() });
      toast.success("Promo code applied.");
      setEditing(false);
      refresh();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this company's promo code?")) return;
    setBusy(true);
    try { await platformApi.clearPromo(company.id); toast.success("Promo code removed."); refresh(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-4" data-testid="drawer-promo-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-200"><Tag className="h-3.5 w-3.5 text-amber-400" /> Promo code</div>
        {!editing && (
          <div className="flex items-center gap-1.5">
            <MiniBtn icon={Pencil} label={active ? "Edit" : "Apply"} onClick={startEdit} testid="drawer-promo-edit-btn" />
            {active && <MiniBtn icon={XIcon} label="Remove" tone="rose" onClick={remove} testid="drawer-promo-remove-btn" />}
          </div>
        )}
      </div>

      {editing ? (
        <div className="grid grid-cols-3 gap-2 items-end">
          <DInput label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="WELCOME25" testid="drawer-promo-code-input" />
          <DInput label="Discount %" type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} testid="drawer-promo-pct-input" />
          <DInput label="Expires" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} testid="drawer-promo-expiry-input" />
          <div className="col-span-3 flex items-center gap-2 mt-1">
            <button onClick={apply} disabled={busy} data-testid="drawer-promo-save-btn" className="px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12px] font-semibold disabled:opacity-50">Save</button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-[12px]">Cancel</button>
          </div>
        </div>
      ) : active ? (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]" data-testid="drawer-promo-active">
          <span className="font-mono font-bold text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">{company.promoCode}</span>
          <span className="text-slate-300">{company.promoDiscountPercent}% off AI Credits</span>
          <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> Expires {fmtDate(company.promoExpiresAt)} · {daysRemaining}d left</span>
        </div>
      ) : (
        <div className="text-[12.5px] text-slate-500" data-testid="drawer-promo-none">No promo code applied</div>
      )}
    </div>
  );
}

function InvoicesTab({ data, company, onNew, refresh }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { err(e); } };
  const payLink = async (inv) => {
    try { const r = await platformApi.payLink(inv.id, window.location.origin); await navigator.clipboard?.writeText(r.url); toast.success("Payment link copied to clipboard."); }
    catch (e) { err(e); }
  };
  const markPaid = async (inv) => {
    const method = window.prompt("Payment method (cash / check / wire / card):", "check");
    if (!method) return;
    act(() => platformApi.markInvoicePaid(inv.id, { method }), "Marked paid.");
  };
  return (
    <div>
      <PromoCodeSection company={company} refresh={refresh} />
      <button data-testid="invoice-new" onClick={onNew} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12.5px] font-semibold mb-3"><Plus className="h-4 w-4" /> New invoice</button>
      <div className="space-y-2">
        {(data.invoices || []).map((inv) => (
          <div key={inv.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`invoice-row-${inv.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[13px] font-semibold text-slate-100">{money2(inv.amount)} · {inv.id}</div>
                <div className="text-[11px] text-slate-500">{inv.kind} · {fmtDate(inv.createdAt)}{inv.lastSentTo ? ` · sent to ${inv.lastSentTo}` : ""}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full border text-[10.5px] font-semibold capitalize ${STATUS_PILL[inv.status] || STATUS_PILL.sent}`}>{inv.status}</span>
            </div>
            {Array.isArray(inv.lineItems) && inv.lineItems.length > 0 && (
              <div className="mb-2 rounded-lg bg-white/[0.03] border border-white/10 divide-y divide-white/5" data-testid={`invoice-lines-${inv.id}`}>
                {inv.lineItems.map((li, idx) => (
                  <div key={idx} className="flex items-center justify-between px-2.5 py-1.5 text-[11.5px]">
                    <span className="text-slate-400">{li.description}</span>
                    <span className="font-semibold text-slate-200">{money2(li.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {(inv.paidAt || inv.paymentMethod) && (
              <div className="mb-2 text-[10.5px] text-emerald-300/80">Paid {inv.paidAt ? fmtDate(inv.paidAt) : ""}{inv.paymentMethod ? ` · ${inv.paymentMethod}` : ""}</div>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              <MiniBtn icon={FileText} label="PDF" onClick={() => window.open(`${API}/api/platform/invoices/${inv.id}/pdf`, "_blank")} testid={`inv-pdf-${inv.id}`} />
              <MiniBtn icon={Send} label="Email" onClick={() => act(() => platformApi.emailInvoice(inv.id), "Invoice emailed.")} testid={`inv-email-${inv.id}`} />
              {inv.status !== "paid" && inv.status !== "void" && <>
                <MiniBtn icon={Link2} label="Pay link" onClick={() => payLink(inv)} testid={`inv-paylink-${inv.id}`} />
                <MiniBtn icon={CheckCircle2} label="Mark paid" onClick={() => markPaid(inv)} testid={`inv-markpaid-${inv.id}`} />
                <MiniBtn icon={Ban} label="Void" tone="rose" onClick={() => act(() => platformApi.voidInvoice(inv.id), "Invoice voided.")} testid={`inv-void-${inv.id}`} />
              </>}
            </div>
          </div>
        ))}
        {!(data.invoices || []).length && <div className="px-3 py-6 text-center text-[12.5px] text-slate-500 rounded-xl border border-white/10">No invoices yet.</div>}
      </div>
    </div>
  );
}

function MiniBtn({ icon: Icon, label, onClick, tone, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11.5px] font-medium ${tone === "rose" ? "border-rose-500/30 text-rose-300 hover:bg-rose-500/10" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

// ---------- Activity ----------
function ActivityTab({ activity }) {
  return (
    <div className="rounded-xl border border-white/10 divide-y divide-white/[0.06]" data-testid="drawer-activity">
      {(activity || []).map((a) => (
        <div key={a.id} className="px-3 py-2.5 flex items-start justify-between gap-3">
          <div>
            <div className="text-[12.5px] text-slate-200">{a.detail}</div>
            <div className="text-[10.5px] text-slate-500">{a.by}</div>
          </div>
          <div className="text-[10.5px] text-slate-500 whitespace-nowrap">{fmtDateTime(a.at)}</div>
        </div>
      ))}
      {!(activity || []).length && <div className="px-3 py-6 text-center text-[12.5px] text-slate-500">No activity yet.</div>}
    </div>
  );
}

// ---------- Notes ----------
function NotesTab({ company, notes, refresh }) {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try { await platformApi.addNote(company.id, text.trim()); setText(""); refresh(); } catch (e) { err(e); }
  };
  const remove = async (noteId) => { try { await platformApi.deleteNote(company.id, noteId); refresh(); } catch (e) { err(e); } };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input value={text} data-testid="note-input" onChange={(e) => setText(e.target.value)} placeholder="Add an internal note…"
          className="flex-1 h-10 px-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30" />
        <button data-testid="note-add" onClick={add} className="px-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[12.5px] font-semibold">Add</button>
      </div>
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-start justify-between gap-3" data-testid={`note-${n.id}`}>
            <div>
              <div className="text-[12.5px] text-slate-200">{n.text}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">{n.by} · {fmtDateTime(n.at)}</div>
            </div>
            <button onClick={() => remove(n.id)} className="p-1 rounded text-rose-300 hover:bg-rose-500/10" data-testid={`note-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {!notes.length && <div className="px-3 py-6 text-center text-[12.5px] text-slate-500 rounded-xl border border-white/10">No notes.</div>}
      </div>
    </div>
  );
}

// ---------- Modals ----------
function FormModal({ title, onClose, onSubmit, submitLabel = "Save", busy, testid, children }) {
  return (
    <Modal title={title} onClose={onClose} testid={testid}>
      <form onSubmit={onSubmit} className="space-y-3.5">
        {children}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={busy} data-testid="form-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[13px] font-semibold disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const ROLES = [["admin", "Admin"], ["dispatcher", "Dispatcher"], ["technician", "Technician"], ["office_personnel", "Office personnel"]];

function AddUserModal({ id, onClose, onDone }) {
  const [f, setF] = useState({ email: "", name: "", password: "", role: "dispatcher" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await platformApi.createCompanyUser(id, { ...f, email: f.email.trim().toLowerCase() }); toast.success("User created."); onDone(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title="Add user" onClose={onClose} onSubmit={submit} submitLabel="Create user" busy={busy} testid="modal-add-user">
      <DInput label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required testid="adduser-email" />
      <DInput label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="adduser-name" />
      <DInput label="Temp password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required testid="adduser-password" />
      <DSelect label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} options={ROLES} testid="adduser-role" />
    </FormModal>
  );
}

function InviteModal({ id, onClose, onDone }) {
  const [f, setF] = useState({ email: "", name: "", role: "dispatcher" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { const r = await platformApi.inviteCompanyUser(id, { ...f, email: f.email.trim().toLowerCase() });
      toast.success(r.email_sent ? "Invite emailed." : "Invite created (email not configured — link copied).");
      if (!r.email_sent && r.accept_url) navigator.clipboard?.writeText(r.accept_url);
      onDone(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title="Invite user by email" onClose={onClose} onSubmit={submit} submitLabel="Send invite" busy={busy} testid="modal-invite">
      <DInput label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required testid="invite-email" />
      <DInput label="Name (optional)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="invite-name" />
      <DSelect label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} options={ROLES} testid="invite-role" />
    </FormModal>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const [f, setF] = useState({ name: user.name || "", email: user.email, role: user.role });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await platformApi.updateUser(user.id, { name: f.name, email: f.email.trim().toLowerCase(), role: f.role }); toast.success("User updated."); onDone(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title={`Edit ${user.email}`} onClose={onClose} onSubmit={submit} submitLabel="Save" busy={busy} testid="modal-edit-user">
      <DInput label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="edituser-name" />
      <DInput label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="edituser-email" />
      <DSelect label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} options={ROLES} testid="edituser-role" />
    </FormModal>
  );
}

function ResetPwModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await platformApi.resetPasswordManual(user.id, pw); toast.success("Password updated."); onClose(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title={`Set password — ${user.email}`} onClose={onClose} onSubmit={submit} submitLabel="Set password" busy={busy} testid="modal-reset-pw">
      <DInput label="New password" value={pw} onChange={(e) => setPw(e.target.value)} required testid="resetpw-password" />
    </FormModal>
  );
}

function EmailAccountModal({ id, onClose }) {
  const [f, setF] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { const r = await platformApi.emailAccount(id, f); toast.success(r.sent ? `Sent to ${r.sent} admin(s).` : "Queued (email not configured)."); onClose(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title="Email account admins" onClose={onClose} onSubmit={submit} submitLabel="Send email" busy={busy} testid="modal-email-account">
      <DInput label="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} required testid="email-subject" />
      <DTextarea label="Message" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={6} testid="email-body" />
    </FormModal>
  );
}

function GrantDaysModal({ id, onClose, onDone }) {
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await platformApi.grantDays(id, Number(days) || 1); toast.success(`Granted ${days} days.`); onDone(); }
    catch (er) { err(er); setBusy(false); } };
  return (
    <FormModal title="Grant free days" onClose={onClose} onSubmit={submit} submitLabel="Grant days" busy={busy} testid="modal-grant-days">
      <DInput label="Days to add" type="number" value={days} onChange={(e) => setDays(e.target.value)} required testid="grant-days-input" />
      <p className="text-[12px] text-slate-500">Extends the trial or current billing period and reactivates the account.</p>
    </FormModal>
  );
}

function InvoiceModal({ id, company, plans, onClose, onDone }) {
  const [kind, setKind] = useState("subscription");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [seatsToAdd, setSeatsToAdd] = useState(1);
  const [busy, setBusy] = useState(false);
  const plan = plans.find((p) => p.id === company.planId);
  const seatPrice = plan ? (company.term === "annual" ? plan.perSeatAnnual : plan.perSeatMonthly) : 0;
  const discountFactor = 1 - (company.discountPct || 0) / 100;
  const subAmount = Math.round(seatPrice * (company.seats || 1) * discountFactor * 100) / 100;
  const addN = Math.max(0, parseInt(seatsToAdd, 10) || 0);
  const curSub = Math.round(seatPrice * (company.seats || 1) * discountFactor * 100) / 100;
  const addSub = Math.round(seatPrice * addN * discountFactor * 100) / 100;
  const per = company.term === "annual" ? "yr" : "mo";

  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try {
      let body;
      if (kind === "subscription") body = { kind: "subscription" };
      else if (kind === "seats") body = { kind: "seats", seatsToAdd: addN };
      else body = { kind: "custom", description: desc, amount: Number(amount) || 0 };
      await platformApi.createInvoice(id, body);
      toast.success(kind === "seats" ? "Seat invoice created — added to next cycle." : "Invoice created."); onDone();
    } catch (er) { err(er); setBusy(false); } };

  return (
    <FormModal title="New invoice" onClose={onClose} onSubmit={submit} submitLabel="Create invoice" busy={busy} testid="modal-invoice">
      <DSelect label="Type" value={kind} onChange={(e) => setKind(e.target.value)} options={[["subscription", "Subscription (auto)"], ["seats", "Add seats"], ["custom", "Custom charge"]]} testid="invoice-kind" />
      {kind === "subscription" && (
        <div className="px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-slate-300">
          {plan ? <>Bills <strong>{money2(subAmount)}</strong> for {company.seats} seat(s) of <strong>{plan.name}</strong> ({company.term}{company.discountPct ? `, ${company.discountPct}% off` : ""}).</> : "No plan set for this company."}
        </div>
      )}
      {kind === "seats" && (
        plan ? (
          <>
            <DInput label="Number of seats to add" type="number" min="1" value={seatsToAdd} onChange={(e) => setSeatsToAdd(e.target.value)} required testid="invoice-seats-add" />
            <div className="px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-slate-300 space-y-1" data-testid="invoice-seats-preview">
              <div className="flex justify-between"><span>Number of Current Seats — {company.seats} × ${seatPrice}/{per}</span><span className="font-semibold text-slate-200">{money2(curSub)}</span></div>
              <div className="flex justify-between"><span>Number of Added Seats — {addN} × ${seatPrice}/{per}</span><span className="font-semibold text-emerald-300">{money2(addSub)}</span></div>
              <div className="flex justify-between pt-1 mt-1 border-t border-white/10"><span className="font-semibold">Invoice total</span><span className="font-bold text-white" data-testid="invoice-seats-total">{money2(curSub + addSub)}</span></div>
              <div className="text-[11px] text-slate-400 pt-1">On auto-pay, the added seats roll into the next billing cycle (from the original start date).</div>
            </div>
          </>
        ) : <div className="px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-slate-300">No plan set for this company.</div>
      )}
      {kind === "custom" && (
        <>
          <DInput label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} required testid="invoice-desc" />
          <DInput label="Amount ($)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required testid="invoice-amount" />
        </>
      )}
    </FormModal>
  );
}
